<template>
	<div class="bg-gray-50">
		<div v-if="!isLoading && !showVehicle">
			<VehicleUnavailable :product="product" />
		</div>

		<div v-if="!isLoading && showVehicle">
			<ContainedSection
				:has-background="true"
				:has-padding="false"
				background-color="bg-white"
			>
				<VehicleDetailsSubscription />
			</ContainedSection>

			<ContainedSection :has-background="true">
				<AtAGlance :product="product" />
			</ContainedSection>

			<TopFeatures class="mt-10" :product="product" />

			<ContainedSection
				v-if="features.length"
				:has-background="true"
				background-color="bg-transparent md:bg-white"
				contained-class="py-10 px-2 md:py-20 md:px-10"
			>
				<VehicleFeatures :product="product" :page-type="'subscribe'" />
			</ContainedSection>

			<ContainedSection :has-padding="false" :has-background="true">
				<SellingOrUpgradingCar heading="Sell your car to subscribe" />
			</ContainedSection>

			<ContainedSection
				:has-background="true"
				background-color="bg-white"
				:has-padding="false"
				custom-class="text-center contained-section"
			>
				<Elfsight />
			</ContainedSection>

			<DefaultSection
				v-if="!isListingOnly"
				class="mt-10 py-10"
				container-class="px-0 md:px-5 lg:px-10"
			>
				<SimilarVehicles
					:product="product"
					:show-more-threshold-limit="3"
					:show-slide-arrows="false"
					@loaded="onSimilarVehiclesLoad"
				/>
			</DefaultSection>

			<ContainedSection :has-padding="false" :has-background="true">
				<StartSubscriptionJourney
					heading="Start your subscription journey here"
				/>
			</ContainedSection>
		</div>

		<ContainedSection
			v-if="!isLoading"
			:has-padding="false"
			class="px-2 py-10 md:py-20"
		>
			<FaqsSubscribe />
		</ContainedSection>

		<div
			v-if="isLoading"
			class="flex min-h-screen w-full flex-wrap items-center justify-center"
		>
			<CarbarPreloader />
		</div>
	</div>
</template>

<script setup>
import ContainedSection from '@/components/sections/ContainedSection.vue'
import StartSubscriptionJourney from '@/components/sections/StartSubscriptionJourney.vue'
import AtAGlance from '@/components/vehicle-details/AtAGlance.vue'
import VehicleFeatures from '@/components/vehicle-details/VehicleFeatures.vue'
import SimilarVehicles from '@/components/vehicle-details/SimilarVehicles.vue'
import TopFeatures from '@/components/vehicle-details/TopFeatures.vue'
import VehicleDetailsSubscription from '@/components/vehicle-details/VehicleDetailsSubscription.vue'
import { useProductDetails } from '@/stores/product-details'
import { useProductCatalog } from '@/stores/product-catalog'
import DefaultSection from '@/components/sections/DefaultSection.vue'
import SellingOrUpgradingCar from '@/components/home/SellingOrUpgradingCar.vue'
import CarbarPreloader from '@/components/preloaders/CarbarPreloader.vue'
import VehicleUnavailable from '@/components/car-unavailable/VehicleUnavailable.vue'
import FaqsSubscribe from '@/components/vehicle-details/FaqsSubscribe.vue'
import Elfsight from '@/components/elfsight/Elfsight.vue'

const runtimeConfig = useRuntimeConfig()
const productDetails = useProductDetails()

const productCatalog = useProductCatalog()

const route = useRoute()
const router = useRouter()

const hasSimilarVehicles = ref(false)
const isListingOnly = useIsListingOnly()

const loadMessage = ref(false)

const productStockKey = `${route.params.webref_code}-${route.params.stock_id}`

// Move onmounted above top-level await to prevent empty component instance
onMounted(async () => {
	scrollToTop()

	// handle the client side fetch
	if (!product.value.pricing) {
		await productDetails.reset()
		await fetchProduct()
	}

	if (route.hash === '#subscribe-now') {
		if (isLoggedIn()) {
			router.push({ hash: '' })
		}

		await startSubscription()
	}

	if (showVehicle.value) {
		productDetails.gtmTrack({
			event: 'view_subscription_vehicle',
			product_category: 'Subscription',
			product: product.value,
			page: productCatalog.productsPagination.currentPage,
			subscription_car_primary_url:
				runtimeConfig.public.APP_URL + route.path,
		})
	}
})

onBeforeUnmount(async () => {
	await productDetails.reset()
})

// Fetch product details on top-level server-side before serving the page
// to client-side so SEO scrapers can see the content rather than an empty
// loading spinner. Use await useAsyncData to return and sync product details to Pinia.
await useAsyncData(`product-${productStockKey}`, async () => {
	if (!productDetails.product?.pricing) {
		await productDetails.fetchProductByDealerCodeStockId(productStockKey)
	}
	return productDetails.product
})

const product = computed(() => {
	if (!productDetails.product) {
		return null
	}

	return productDetails.product
})

const showVehicle = computed(
	() => product.value && product.value.pricing && product.value.is_available
)

const isLoading = computed(() => productDetails.isLoading)

const features = computed(() => {
	if (!product.value.features || product.value.features.length === 0)
		return []

	return product.value.features.filter((feature) => !!feature.category)
})

const metaTitle = computed(() => {
	if (!product.value) return 'Car Subscriptions | carbar Australia'
	return `Subscribe to a ${product.value.year} ${product.value.make} ${product.value.model} for $${product.value.pricing?.price}/wk | carbar Australia`
})

const metaDescription = computed(() => {
	if (!product.value)
		return 'Get a flexible car subscription with carbar Australia. No loans, no long-term commitments. Subscribe now!'
	return `Get a ${product.value.year} ${product.value.make} ${product.value.model} with a flexible car subscription. No loans, no long-term commitments. Subscribe now!`
})

const metaImage = computed(() => {
	return product.value?.primary_image
		? product.value.primary_image
		: `${runtimeConfig.public.APP_URL}/_ipx/q_100/img/product-catalog/img-comingsoon-xl.webp`
})

useSeoMeta({
	title: () => metaTitle.value,
	description: () => metaDescription.value,
	ogTitle: () => metaTitle.value,
	ogDescription: () => metaDescription.value,
	ogImage: () => metaImage.value,
	twitterTitle: () => metaTitle.value,
	twitterDescription: () => metaDescription.value,
	twitterImage: () => metaImage.value,
	robots: () => 'noindex, nofollow',
})

const fetchProduct = async () => {
	await productDetails.fetchProductByDealerCodeStockId(productStockKey)
}

const onSimilarVehiclesLoad = async (similarProducts) => {
	hasSimilarVehicles.value = !!similarProducts.length
	if (loadMessage.value) {
		await startSubscription()
	}
}

const startSubscription = async () => {
	if (isLoggedIn()) {
		await productDetails.checkExistingOrders()
	} else {
		productDetails.onModalOpen()
	}
}
</script>
